import React, { useState, useEffect } from 'react';
import { Search, Trash2, Edit, Plus, Save, X, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
const ProfessionManagement = () => {
  const navigate = useNavigate();
  const [professions, setProfessions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    ProfessionName: '',
    Description: '',
    Notes: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [showNotes, setShowNotes] = useState(null);

  useEffect(() => {
    fetchProfessions();
  }, []);

  const fetchProfessions = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/professions`);
      if (!response.ok) {
        throw new Error('فشل في جلب بيانات المهن');
      }
      const data = await response.json();
      setProfessions(data);
    } catch (error) {
      console.error('خطأ في جلب المهن:', error);
      alert('حدث خطأ في جلب بيانات المهن');
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingId 
      ? `${config.API_BASE_URL}/api/professions/${editingId}`
      : `${config.API_BASE_URL}/api/professions`;
        
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'حدث خطأ في العملية');
      }

      alert(editingId ? 'تم تحديث المهنة بنجاح' : 'تم إضافة المهنة بنجاح');
      await fetchProfessions();
      resetForm();
    } catch (error) {
      console.error('خطأ:', error);
      alert('حدث خطأ: ' + error.message);
    }
  };

  const handleEdit = (profession) => {
    setFormData({
      ProfessionName: profession.ProfessionName,
      Description: profession.Description || '',
      Notes: profession.Notes || ''
    });
    setEditingId(profession.ProfessionID);
    setShowForm(true);
  };

  const handleDelete = async (professionId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه المهنة؟')) {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/professions/${professionId}`, {
          method: 'DELETE'
      });
        
        if (!response.ok) {
          throw new Error('فشل في حذف المهنة');
        }

        alert('تم حذف المهنة بنجاح');
        fetchProfessions();
      } catch (error) {
        console.error('خطأ في حذف المهنة:', error);
        alert('حدث خطأ في حذف المهنة');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      ProfessionName: '',
      Description: '',
      Notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const goBack = () => {
    navigate('/');
  };

  const filteredProfessions = professions.filter(profession =>
    profession.ProfessionName?.toLowerCase().includes(search.toLowerCase()) ||
    profession.Description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100 py-3 md:py-6 overflow-x-hidden" dir="rtl">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl md:rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
            <div>
              <p className="md:hidden text-xs text-blue-600 font-semibold mb-1">مسار / المهن</p>
              <h1 className="text-xl md:text-3xl font-bold text-gray-900">إدارة المهن</h1>
              <p className="md:hidden text-sm text-gray-500 mt-1">إجمالي {filteredProfessions.length} مهنة</p>
            </div>
            <button 
              onClick={goBack}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 md:px-6 py-3 md:py-2.5 rounded-xl md:rounded-lg transition duration-200 ease-in-out md:transform md:hover:scale-105 w-full md:w-auto"
            >
              العودة للرئيسية
            </button>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-2xl md:rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 md:px-6 py-3 md:py-2.5 rounded-xl md:rounded-lg flex items-center justify-center gap-2 transition duration-200 ease-in-out md:transform md:hover:scale-105 w-full md:w-auto font-semibold"
              onClick={() => setShowForm(true)}
            >
              <Plus size={20} />
              إضافة مهنة جديدة
            </button>
            <div className="relative w-full md:w-96">
              <Search className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث في المهن..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-xl md:rounded-lg py-3 md:py-2.5 px-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
              />
            </div>
          </div>
        </div>

        {/* Professions Table */}
        <div className="bg-white rounded-2xl md:rounded-lg shadow-md overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 border-b border-gray-200 text-right">اسم المهنة</th>
                  <th className="px-6 py-4 border-b border-gray-200 text-right">الوصف</th>
                  <th className="px-6 py-4 border-b border-gray-200 text-right">ملاحظات</th>
                  <th className="px-6 py-4 border-b border-gray-200 text-right">تاريخ الإضافة</th>
                  <th className="px-6 py-4 border-b border-gray-200 text-right">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfessions.map((profession) => (
                  <tr key={profession.ProfessionID} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 border-b border-gray-200 font-medium">
                      {profession.ProfessionName}
                    </td>
                    <td className="px-6 py-4 border-b border-gray-200">
                      {profession.Description || '-'}
                    </td>
                    <td className="px-6 py-4 border-b border-gray-200">
                      {profession.Notes ? (
                        <button
                          onClick={() => setShowNotes(profession)}
                          className="text-blue-500 hover:text-blue-700 flex items-center gap-1"
                        >
                          <FileText size={16} />
                          <span>عرض الملاحظات</span>
                        </button>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 border-b border-gray-200">
                      {new Date(profession.CreatedAt).toLocaleDateString('ar-EG')}
                    </td>
                    <td className="px-6 py-4 border-b border-gray-200">
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() => handleEdit(profession)}
                          className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded-full transition duration-200"
                          title="تعديل"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(profession.ProfessionID)}
                          className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-full transition duration-200"
                          title="حذف"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden p-3 space-y-3">
            {filteredProfessions.map((profession) => (
              <div key={profession.ProfessionID} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="h-1.5 bg-gradient-to-l from-indigo-500 to-purple-500" />
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-900 break-words">{profession.ProfessionName}</h3>
                      <p className="text-xs text-gray-500 mt-1">تاريخ الإضافة: {profession.CreatedAt ? new Date(profession.CreatedAt).toLocaleDateString('ar-EG') : '-'}</p>
                    </div>
                    <span className="shrink-0 bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded-full">مهنة</span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-sm"><span className="text-gray-500">الوصف: </span><span className="font-medium break-words">{profession.Description || '-'}</span></div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => handleEdit(profession)} className="bg-blue-50 text-blue-700 border border-blue-100 rounded-xl py-2.5 flex items-center justify-center gap-1 font-semibold text-sm"><Edit size={15} /> تعديل</button>
                    <button onClick={() => setShowNotes(profession)} disabled={!profession.Notes} className="bg-amber-50 text-amber-700 border border-amber-100 rounded-xl py-2.5 flex items-center justify-center gap-1 font-semibold text-sm disabled:opacity-50"><FileText size={15} /> ملاحظات</button>
                    <button onClick={() => handleDelete(profession.ProfessionID)} className="bg-red-50 text-red-700 border border-red-100 rounded-xl py-2.5 flex items-center justify-center gap-1 font-semibold text-sm"><Trash2 size={15} /> حذف</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch md:items-center justify-center z-50 md:p-4">
            <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full md:max-w-2xl md:mx-4 h-[100dvh] md:h-auto overflow-y-auto">
              <div className="p-4 md:p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingId ? 'تعديل المهنة' : 'إضافة مهنة جديدة'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600 transition duration-200"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="p-4 md:p-6 pb-24 md:pb-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">اسم المهنة</label>
                    <input
                      type="text"
                      value={formData.ProfessionName}
                      onChange={(e) => setFormData({...formData, ProfessionName: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
                    <textarea
                      value={formData.Description}
                      onChange={(e) => setFormData({...formData, Description: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 h-24 resize-none"
                      placeholder="أدخل وصفاً مختصراً للمهنة..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات</label>
                    <textarea
                      value={formData.Notes}
                      onChange={(e) => setFormData({...formData, Notes: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 h-24 resize-none"
                      placeholder="أضف أي ملاحظات إضافية..."
                    />
                  </div>

                  <div className="fixed md:static bottom-0 left-0 right-0 bg-white border-t p-3 md:p-0 flex flex-col-reverse md:flex-row justify-end gap-2 md:gap-4 md:pt-6 md:border-t border-gray-200">
                    <button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 md:py-2.5 rounded-xl md:rounded-lg flex items-center justify-center gap-2 transition duration-200 ease-in-out md:transform md:hover:scale-105 w-full md:w-auto"
                    >
                      <Save size={20} />
                      {editingId ? 'تحديث المهنة' : 'حفظ المهنة'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 md:py-2.5 rounded-xl md:rounded-lg transition duration-200 w-full md:w-auto"
                    >
                      إلغاء
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {showNotes && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNotes(null)}
          >
            <div 
              className="bg-white rounded-none md:rounded-xl shadow-2xl w-full h-[100dvh] md:h-auto md:max-w-2xl md:mx-4 p-4 md:p-6 overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  ملاحظات: {showNotes.ProfessionName}
                </h3>
                <button
                  onClick={() => setShowNotes(null)}
                  className="text-gray-400 hover:text-gray-600 transition duration-200"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 min-h-[100px] max-h-[400px] overflow-y-auto">
                {showNotes.Notes || 'لا توجد ملاحظات'}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowNotes(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2.5 rounded-lg transition duration-200"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        )}
{/* Empty State */}
{filteredProfessions.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد مهن</h3>
            <p className="text-gray-500 mb-4">لم يتم إضافة أي مهن حتى الآن، يمكنك إضافة مهنة جديدة من خلال الزر أعلاه.</p>
            <button 
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg inline-flex items-center gap-2 transition duration-200"
            >
              <Plus size={20} />
              إضافة مهنة جديدة
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionManagement;